import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface Usuario {
  id?: string;
  nombre: string;
  dpi?: string;
  tipo: 'comprador' | 'vendedor' | 'socio' | 'admin' | 'contabilidad';
  pin: string;
  password?: string; // Para admin y socio
  lotesAsignados?: string[];
  activo: boolean;
  fechaCreacion: string;
  creadoPor: string;
  email?: string;
  telefono?: string;
  direccion?: string;
  ultimoAcceso?: string;
  intentosFallidos?: number;
  bloqueado?: boolean;
  permisos?: string[];
}

export function useUsuarios() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar usuarios desde Firestore
  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'usuarios'));
      const usuariosData: Usuario[] = [];
      querySnapshot.forEach((doc) => {
        usuariosData.push({ id: doc.id, ...doc.data() } as Usuario);
      });
      setUsuarios(usuariosData);
      setError(null);
    } catch (err) {
      console.error('Error fetching usuarios:', err);
      setError('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  // Suscripción en tiempo real a los usuarios
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'usuarios'), (snapshot) => {
      const usuariosData: Usuario[] = [];
      snapshot.forEach((doc) => {
        usuariosData.push({ id: doc.id, ...doc.data() } as Usuario);
      });
      setUsuarios(usuariosData);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to usuarios:', err);
      setError('Error al sincronizar usuarios');
    });

    return () => unsubscribe();
  }, []);

  // Agregar un usuario
  const addUsuario = useCallback(async (usuario: Omit<Usuario, 'id'>) => {
    try {
      console.log('useUsuarios: Iniciando addUsuario con datos:', usuario);
      
      // Filtrar campos undefined para evitar errores de Firestore
      const usuarioLimpio = Object.fromEntries(
        Object.entries(usuario).filter(([_, value]) => value !== undefined)
      );
      
      const usuarioData = {
        ...usuarioLimpio,
        fechaCreacion: new Date().toISOString(),
        activo: true,
        intentosFallidos: 0,
        bloqueado: false
      };
      
      console.log('useUsuarios: Datos procesados para Firestore:', usuarioData);
      
      const docRef = await addDoc(collection(db, 'usuarios'), usuarioData);
      console.log('useUsuarios: Usuario agregado exitosamente con ID:', docRef.id);
      
      const newUsuario = { id: docRef.id, ...usuarioData };
      return docRef.id;
    } catch (err) {
      console.error('useUsuarios: Error adding usuario:', err);
      setError('Error al agregar usuario');
      throw err;
    }
  }, []);

  // Editar un usuario
  const updateUsuario = useCallback(async (id: string, updates: Partial<Usuario>) => {
    try {
      // Filtrar campos undefined para evitar errores de Firestore
      const updatesLimpio = Object.fromEntries(
        Object.entries(updates).filter(([_, value]) => value !== undefined)
      );
      
      const updateData = {
        ...updatesLimpio,
        ultimoAcceso: new Date().toISOString()
      };
      await updateDoc(doc(db, 'usuarios', id), updateData);
    } catch (err) {
      console.error('Error updating usuario:', err);
      setError('Error al actualizar usuario');
      throw err;
    }
  }, []);

  // Eliminar un usuario
  const deleteUsuario = useCallback(async (id: string) => {
    try {
      await deleteDoc(doc(db, 'usuarios', id));
    } catch (err) {
      console.error('Error deleting usuario:', err);
      setError('Error al eliminar usuario');
      throw err;
    }
  }, []);

  // Cambiar estado activo/inactivo
  const toggleActivo = useCallback(async (id: string, activo: boolean) => {
    try {
      await updateDoc(doc(db, 'usuarios', id), { activo });
    } catch (err) {
      console.error('Error toggling usuario activo:', err);
      setError('Error al cambiar estado del usuario');
      throw err;
    }
  }, []);

  // Generar nuevo PIN
  const generarNuevoPin = useCallback(async (id: string) => {
    try {
      const nuevoPin = Math.floor(1000 + Math.random() * 9000).toString();
      await updateDoc(doc(db, 'usuarios', id), { 
        pin: nuevoPin,
        intentosFallidos: 0,
        bloqueado: false
      });
      return nuevoPin;
    } catch (err) {
      console.error('Error generating new PIN:', err);
      setError('Error al generar nuevo PIN');
      throw err;
    }
  }, []);

  // Buscar usuario por credenciales (para login)
  const buscarPorCredenciales = useCallback((identificador: string, pin: string): Usuario | null => {
    return usuarios.find(usuario => 
      (usuario.nombre.toLowerCase() === identificador.toLowerCase() || 
       usuario.dpi === identificador) && 
      usuario.pin === pin && 
      usuario.activo && 
      !usuario.bloqueado
    ) || null;
  }, [usuarios]);

  // Registrar intento fallido de login
  const registrarIntentoFallido = useCallback(async (id: string) => {
    try {
      const usuario = usuarios.find(u => u.id === id);
      if (!usuario) return;

      const nuevosIntentos = (usuario.intentosFallidos || 0) + 1;
      const bloqueado = nuevosIntentos >= 3;

      await updateDoc(doc(db, 'usuarios', id), { 
        intentosFallidos: nuevosIntentos,
        bloqueado
      });
    } catch (err) {
      console.error('Error registering failed attempt:', err);
    }
  }, [usuarios]);

  // Resetear intentos fallidos
  const resetearIntentosFallidos = useCallback(async (id: string) => {
    try {
      await updateDoc(doc(db, 'usuarios', id), { 
        intentosFallidos: 0,
        bloqueado: false
      });
    } catch (err) {
      console.error('Error resetting failed attempts:', err);
    }
  }, []);

  return {
    usuarios,
    loading,
    error,
    fetchUsuarios,
    addUsuario,
    updateUsuario,
    deleteUsuario,
    toggleActivo,
    generarNuevoPin,
    buscarPorCredenciales,
    registrarIntentoFallido,
    resetearIntentosFallidos
  };
} 